/**
 * Integration product-link API (Integrations 1A page).
 */
import fetchWithAuth from "@/api/secureFetch";

const AGENT_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

export type IntegrationTargetType = "product" | "context_mesh";

export interface IntegrationTargetDto {
    type: IntegrationTargetType;
    id: string | number;
    name: string;
}

export interface IntegrationLinkDto {
    id: number | string;
    externalRef: string;
    externalName: string;
    metadata: Record<string, unknown>;
    /** Present for legacy Product links. New clients should use target. */
    projectId?: number;
    target?: IntegrationTargetDto;
    syncedCount: number | null;
    syncedUnit: string | null;
    lastSyncedAt: string | null;
    syncStatus: "needs_review" | "synced" | "failed";
    lastSyncError: string | null;
}

export interface ContextMeshRefreshNotice {
    context_mesh_refresh?: {
        scheduled: boolean;
        request_id?: string;
    };
    warning?: string;
}

export type ProviderId = "jira" | "github" | "gitlab" | "bitbucket" | "azure-devops" | "azure-repos";
export type IntegrationProviderId = ProviderId;

export interface VendorConnectionDto {
    provider: IntegrationProviderId;
    vendor: string;
    connected: boolean;
    siteOrOrg: string;
    cloudId: string | null;
    organization: string | null;
    links: IntegrationLinkDto[];
}

export interface IntegrationCredentialStatusDto {
    hasCredentials: boolean;
    isActive?: boolean;
    isConnected?: boolean;
    isExpired?: boolean;
    lastSyncAt?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
}

export interface IntegrationApiErrorDetails {
    error?: string;
    errorCode?: string;
    requiresReauth?: boolean;
    provider?: string;
    vendor?: string;
}

export type IntegrationApiError = Error & {
    status: number;
    details?: IntegrationApiErrorDetails;
};

export interface CreateLinksRequest {
    provider: ProviderId;
    externalRef: string;
    externalName: string;
    metadata?: Record<string, unknown>;
    projectIds?: number[];
    target?: Pick<IntegrationTargetDto, "type" | "id">;
}

async function expectOk(response: Response, what: string) {
    if (!response.ok) {
        let body: unknown;
        let detail = "";
        try {
            body = await response.json();
            if (body && typeof body === "object" && typeof (body as { error?: unknown }).error === "string") {
                detail = (body as { error: string }).error;
            }
        } catch { /* body was not JSON */ }
        const error = new Error(detail || `${what} failed: ${response.status}`) as IntegrationApiError;
        error.status = response.status;
        if (body && typeof body === "object") {
            error.details = body as IntegrationApiErrorDetails;
        }
        throw error;
    }
    return response.json();
}

export interface IntegrationTargetsDto {
    products: IntegrationTargetDto[];
    contextMeshes: IntegrationTargetDto[];
}

export async function fetchIntegrationLinks(token: string): Promise<{
    vendors: VendorConnectionDto[];
    targets?: IntegrationTargetsDto;
}> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/integrations/links`, token, { method: "GET" });
    return expectOk(response, "Fetching integration links");
}

export async function fetchIntegrationCredentialStatus(
    vendor: string,
    token: string
): Promise<IntegrationCredentialStatusDto> {
    const query = new URLSearchParams({ vendor });
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/integrations/status?${query.toString()}`,
        token,
        { method: "GET" }
    );
    if (response.status === 404) {
        return { hasCredentials: false };
    }
    return expectOk(response, "Checking integration credentials");
}

export async function createIntegrationLinks(req: CreateLinksRequest, token: string): Promise<{ links: IntegrationLinkDto[] } & ContextMeshRefreshNotice> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/integrations/links`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req)
    });
    return expectOk(response, "Creating integration links");
}

function targetPath(target: IntegrationTargetDto, linkId: number | string): string {
    return `${target.type}/${encodeURIComponent(String(target.id))}/${encodeURIComponent(String(linkId))}`;
}

export async function deleteIntegrationLink(
    link: Pick<IntegrationLinkDto, "id" | "target"> | number,
    token: string
): Promise<{ success: boolean } & ContextMeshRefreshNotice> {
    const path = typeof link === "number"
        ? String(link)
        : link.target
            ? targetPath(link.target, link.id)
            : encodeURIComponent(String(link.id));
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/integrations/links/${path}`, token, { method: "DELETE" });
    return expectOk(response, "Deleting integration link");
}

export async function syncIntegrationLink(
    link: Pick<IntegrationLinkDto, "id" | "target"> | number,
    token: string,
    onProgress: (message: string) => void = () => undefined
): Promise<{ link: IntegrationLinkDto } & ContextMeshRefreshNotice> {
    const path = typeof link === "number"
        ? String(link)
        : link.target
            ? targetPath(link.target, link.id)
            : encodeURIComponent(String(link.id));
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/integrations/links/${path}/sync`, token, {
        method: "POST"
    });
    if (!response.ok) {
        return expectOk(response, "Synchronizing integration link");
    }
    if (!response.body) throw new Error("Synchronization stream was unavailable");

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
        const { done, value } = await reader.read();
        buffer = (buffer + decoder.decode(value, { stream: !done })).replace(/\r\n/g, "\n");

        let boundary = buffer.indexOf("\n\n");
        while (boundary !== -1) {
            const frame = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            boundary = buffer.indexOf("\n\n");

            const event = frame.match(/^event:\s*(.+)$/m)?.[1]?.trim();
            const dataLines = frame.match(/^data:\s*(.*)$/gm) || [];
            if (!event || dataLines.length === 0) continue;

            let payload: Record<string, unknown>;
            try {
                payload = JSON.parse(dataLines.map(line => line.replace(/^data:\s*/, "")).join("\n"));
            } catch {
                continue;
            }

            if (event === "progress") {
                if (typeof payload.message === "string") onProgress(payload.message);
                continue;
            }
            if (event === "error") {
                throw new Error(typeof payload.error === "string" ? payload.error : "Synchronization failed");
            }
            if (event === "complete") {
                if (!payload.link || typeof payload.link !== "object") {
                    throw new Error("Synchronization completed without an updated link");
                }
                return payload as unknown as { link: IntegrationLinkDto } & ContextMeshRefreshNotice;
            }
        }
        if (done) break;
    }

    throw new Error("Synchronization ended before completion");
}

export async function unlinkAllForRef(provider: string, externalRef: string, token: string): Promise<{ success: boolean; deleted: number } & ContextMeshRefreshNotice> {
    const qs = `provider=${encodeURIComponent(provider)}&externalRef=${encodeURIComponent(externalRef)}`;
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/integrations/links?${qs}`, token, { method: "DELETE" });
    return expectOk(response, "Unlinking");
}

export async function listGitRepositories(
    provider: "github" | "gitlab" | "bitbucket" | "azure_repos",
    token: string,
    page = 1,
    perPage = 100
): Promise<{ repositories: Array<{ name: string; full_name?: string; clone_url?: string; url?: string; default_branch?: string }> }> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/git/${provider}/repositories`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ page, perPage })
    });
    return expectOk(response, "Listing repositories");
}

export async function initiateGitProviderOAuth(
    provider: "azure_repos",
    body: Record<string, unknown>,
    token: string
): Promise<{ authUrl?: string; authorizationUrl?: string }> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/git/${provider}/oauth/authorize`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
    });
    return expectOk(response, "Starting provider authorization");
}

export async function disconnectGitProvider(
    provider: "github" | "gitlab" | "bitbucket" | "azure_repos",
    token: string
): Promise<{ success: boolean } & ContextMeshRefreshNotice> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/git/${provider}/credentials/disconnect`, token, { method: "POST" });
    return expectOk(response, "Disconnecting");
}
